import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import styled, { css } from "styled-components";

export const UploadLayout = styled.div`
    display: flex;
    gap: 28px;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                css`
                    flex-direction: column;
                `
            ],
            "max",
        )}
`;

export const UploadLeft = styled.div`
    flex: 1;
`;

export const UploadRight = styled.div`
    flex-shrink: 0;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                css`
                    width: 100%;
                `
            ],
            "max",
        )}
`;

export const InstructionsBox = styled.div`
    background: ${themeBackgrounds.lightGray};
    border: 1px solid ${getColor("common.border")};
    border-radius: 5px;
    padding: 14px 16px;
    ol {
        padding-left: 16px;
        margin: 0;
    }
    li {
        font-size: ${fontSizes.medium};
        line-height: 2;
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

export const ErrorBadge = styled.div`
    margin-top: 8px;
    background: ${themeBackgrounds.danger};
    border: 1px solid ${themeColors.danger};
    border-radius: 4px;
    padding: 8px 12px;
    font-size: ${fontSizes.small};
    color: ${themeColors.danger};
`;

export const ValidateBanner = styled.div<{ variant: "warn" | "ok" }>`
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
    font-size: ${fontSizes.small};
    line-height: 1.5;
    &,
    > div {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    ${({ variant }) =>
        variant === "warn"
            ? `background: ${themeColors.warningBannerBg}; border: 1px solid ${themeColors.warningBannerBorder}; color: ${themeColors.warningBannerText};`
            : `background: ${themeColors.successBannerBg}; border: 1px solid ${themeColors.successBannerBorder}; color: ${themeColors.success};`}

    ${({ theme }: { theme: BaseTheme }) =>
    breakpointMediaQueries(
        theme,
        [
            null,
            null,
            css`
                flex-wrap: wrap;
                button {
                    margin: 0 auto;
                }
            `
        ],
        "max",
    )}
`;


export const PartInput = styled.input<{ hasError?: boolean }>`
    border: 1px solid ${({ hasError }) => (hasError ? themeColors.danger : themeColors.primary.richBlack)};
    border-radius: 4px;
    padding: 8px 16px;
	font-size: ${fontSizes.medium};
	line-height: ${lineHeights.medium};
	color: ${themeColors.primary.richBlack};
    width: 165px;
    background: ${({ hasError }) => (hasError ? themeBackgrounds.danger : themeBackgrounds.white)};
    color: ${({ hasError }) => (hasError ? themeColors.danger : "inherit")};
    &:focus {
		border: 1px solid ${themeColors.primary.richBlack};
		color: ${themeColors.primary.richBlack};
	}
`;

export const QtyInput = styled.input<{ hasError?: boolean }>`
    border: 1px solid ${({ hasError }) => (hasError ? themeColors.danger : themeColors.primary.richBlack)};
    border-radius: 4px;
    padding: 8px 16px;
	font-size: ${fontSizes.medium};
	line-height: ${lineHeights.medium};
	color: ${themeColors.primary.richBlack};
    width: 72px;
    text-align: center;
    background: ${({ hasError }) => (hasError ? themeBackgrounds.danger : themeBackgrounds.white)};
    &:focus {
		border: 1px solid ${themeColors.primary.richBlack};
		color: ${themeColors.primary.richBlack};
	}
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        opacity: 1;
    }
`;

export const DateInput = styled.input<{ hasError?: boolean }>`
    border: 1px solid ${({ hasError }) => (hasError ? themeColors.danger : themeColors.primary.richBlack)};
    border-radius: 4px;
    padding: 8px 10px;
    font-size: ${fontSizes.medium};
    line-height: ${lineHeights.medium};
    color: ${({ hasError }) => (hasError ? themeColors.danger : themeColors.primary.richBlack)};
    width: 148px;
    background: ${({ hasError }) => (hasError ? themeBackgrounds.danger : themeBackgrounds.white)};
    &:focus {
        border: 1px solid ${themeColors.primary.richBlack};
        color: ${themeColors.primary.richBlack};
    }
`;

export const QtyHint = styled.div`
    font-size: ${fontSizes.xSmall};
    margin-top: 3px;
`;

export const QtyError = styled.div`
    font-size: 11px;
    color: ${themeColors.danger};
    margin-top: 3px;
`;

export const StatusBadge = styled.span<{ variant: "valid" | "invalid" | "rtb" }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: ${fontSizes.xSmall};
    font-weight: ${fontWeights.semiBold};
    padding: 2px 8px;
    border-radius: 12px;
    white-space: nowrap;
    ${({ variant }) =>
        variant === "valid"
            ? `background: ${themeColors.successBannerBg}; color: ${themeColors.success};`
            : variant === "rtb"
            ? `background: ${themeColors.warningBannerBg}; color:${themeColors.warningBannerText};`
            : `background: ${themeBackgrounds.danger}; color: ${themeColors.danger};`}
`;

export const BlockWarning = styled.div<{ variant: "invalid" | "rtb" }>`
    margin-top: 16px;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: ${fontSizes.small};
    line-height: 1.5;
    display: flex;
    gap: 8px;
    align-items: center;
    ${({ variant }) =>
        variant === "invalid"
            ? `background: ${themeBackgrounds.danger}; border: 1px solid ${themeColors.danger}; color: ${themeColors.danger};`
            : `background: ${themeColors.warningBannerBg}; border: 1px solid ${themeColors.warningBannerBorder}; color: ${themeColors.warningBannerText}`}
`;

export const ErrMsg = styled.div`
    color: ${themeColors.danger};
    margin-top: 4px;
    font-size: ${fontSizes.small};
`;

export const ReplacementMsg = styled.div`
    font-size: ${fontSizes.small};
    color: ${themeColors.warningBannerText};
    background: ${themeColors.warningBannerBg};
    border: 1px solid ${themeColors.warningBannerBorder};
    border-radius: 4px;
    padding: 6px 10px;
    margin-top: 4px;
`;

export const RtbMsg = styled.div`
    font-size: ${fontSizes.small};
    color: ${themeColors.warningBannerText};
    background: ${themeColors.warningBannerBg};
    border: 1px solid ${themeColors.warningBannerBorder};
    border-radius: 4px;
    padding: 8px 12px;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    a {
        color: ${themeColors.primary.danaBlue}
        font-weight: ${fontWeights.semiBold};
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
`;